import React, { useState, useEffect, useCallback } from 'react';
import {
  FiUsers, FiFileText, FiFolder, FiMessageSquare, FiCheckCircle,
  FiAlertCircle, FiClock, FiAward, FiEdit, FiTrendingUp,
  FiRefreshCw, FiBell, FiArrowLeft,
} from 'react-icons/fi';
import { getAuthHeaders } from '../utils/auth';
import { getApiEndpoint } from '../utils/api';
import './TeamDashboard.css';

const STAGE_LABELS = {
  nofo_input: 'NOFO Input', funder_research: 'Funder Research',
  pi_profile: 'PI Profile', team_building: 'Team Building',
  drafting: 'Drafting', compliance: 'Compliance', done: 'Complete',
  collection_input: 'Setup', outline: 'Outline', author_profile: 'Author Profile',
};

const OUTCOME_CLASS = {
  funded: 'td-outcome--funded',
  rejected: 'td-outcome--rejected',
  pending: 'td-outcome--pending',
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

const TeamDashboard = ({ teamId, teamName, onBack }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [days, setDays] = useState(30);
  const [portfolios, setPortfolios] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        getApiEndpoint(`/api/teams/${teamId}/activity-dashboard?days=${days}`),
        { headers: getAuthHeaders() }
      );
      if (!res.ok) throw new Error('Failed to load dashboard');
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [teamId, days]);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const fetchPortfolios = useCallback(async () => {
    try {
      const res = await fetch(
        getApiEndpoint(`/api/teams/${teamId}/member-portfolios`),
        { headers: getAuthHeaders() }
      );
      if (res.ok) setPortfolios(await res.json());
    } catch (_) {}
  }, [teamId]);

  useEffect(() => { fetchPortfolios(); }, [fetchPortfolios]);

  if (loading) {
    return (
      <div className="td-loading">
        <div className="spinner" />
        <p>Loading team activity...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="td-error">
        <FiAlertCircle /> {error}
        <button onClick={fetchDashboard}><FiRefreshCw /> Retry</button>
      </div>
    );
  }

  if (!data) return null;

  const { totals, member_activity, proposals, drafts, outstanding_items,
          overdue_items, monitor_alerts, inactive_members } = data;

  return (
    <div className="td-page">

      {/* Header */}
      <div className="td-header">
        <div className="td-header-left">
          {onBack && (
            <button className="td-back-btn" onClick={onBack}>
              <FiArrowLeft /> Back
            </button>
          )}
          <div>
            <h2><FiUsers /> {teamName || data.team_name}</h2>
            <p className="td-subtitle">Lab Activity Dashboard</p>
          </div>
        </div>
        <div className="td-period-selector">
          {[7, 14, 30, 90].map(d => (
            <button
              key={d}
              className={`td-period-btn ${days === d ? 'td-period-btn--active' : ''}`}
              onClick={() => setDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div className="td-stats">
        <div className="td-stat">
          <FiFileText className="td-stat-icon" />
          <div>
            <span className="td-stat-value">{totals.papers_added}</span>
            <span className="td-stat-label">Papers Added</span>
          </div>
        </div>
        <div className="td-stat">
          <FiAward className="td-stat-icon" />
          <div>
            <span className="td-stat-value">{totals.active_proposals || 0}</span>
            <span className="td-stat-label">Active Proposals</span>
          </div>
        </div>
        <div className="td-stat">
          <FiEdit className="td-stat-icon" />
          <div>
            <span className="td-stat-value">{totals.active_drafts || 0}</span>
            <span className="td-stat-label">Active Papers</span>
          </div>
        </div>
        <div className="td-stat">
          <FiCheckCircle className="td-stat-icon" />
          <div>
            <span className="td-stat-value">{totals.funded_proposals || 0}</span>
            <span className="td-stat-label">Funded</span>
          </div>
        </div>
        <div className="td-stat">
          <FiFolder className="td-stat-icon" />
          <div>
            <span className="td-stat-value">{totals.collections_modified}</span>
            <span className="td-stat-label">Collections Active</span>
          </div>
        </div>
        <div className="td-stat td-stat--alert">
          <FiAlertCircle className="td-stat-icon" />
          <div>
            <span className="td-stat-value">{totals.overdue_items}</span>
            <span className="td-stat-label">Overdue</span>
          </div>
        </div>
      </div>

      {/* Member activity table */}
      <div className="td-section">
        <h3><FiUsers /> Member Activity <span className="td-muted">(last {days} days)</span></h3>
        <div className="td-table-wrap">
          <table className="td-table">
            <thead>
              <tr>
                <th>Member</th>
                <th>Papers</th>
                <th>Proposals</th>
                <th>Papers (Draft)</th>
                <th>Tasks Done</th>
                <th>Chats</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {member_activity.map(m => (
                <tr key={m.user_id} className={m.is_inactive ? 'td-row--inactive' : ''}>
                  <td className="td-member-name">
                    {m.display_name || m.username}
                    {m.is_inactive && <span className="td-inactive-badge">Inactive</span>}
                  </td>
                  <td>{m.papers_added}</td>
                  <td>
                    {m.active_proposals > 0 && <span className="td-count-badge">{m.active_proposals} active</span>}
                    {m.funded_proposals > 0 && <span className="td-count-badge td-count-badge--funded">{m.funded_proposals} funded</span>}
                    {!m.active_proposals && !m.funded_proposals && <span className="td-muted">0</span>}
                  </td>
                  <td>
                    {m.active_drafts > 0 && <span className="td-count-badge">{m.active_drafts} active</span>}
                    {m.completed_drafts > 0 && <span className="td-count-badge td-count-badge--done">{m.completed_drafts} done</span>}
                    {!m.active_drafts && !m.completed_drafts && <span className="td-muted">0</span>}
                  </td>
                  <td>{m.action_items_completed || <span className="td-muted">0</span>}</td>
                  <td>{m.chat_sessions || <span className="td-muted">0</span>}</td>
                  <td>{m.notes_created || <span className="td-muted">0</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="td-grid">

        {/* Active proposals */}
        <div className="td-section">
          <h3><FiAward /> Grant Proposals</h3>
          {proposals.length === 0 ? (
            <p className="td-empty">No proposals yet</p>
          ) : (
            <div className="td-card-list">
              {proposals.map(p => (
                <div key={p.id} className="td-work-card">
                  <div className="td-work-top">
                    <span className="td-work-owner">{p.owner_username}</span>
                    {p.funding_outcome && (
                      <span className={`td-outcome ${OUTCOME_CLASS[p.funding_outcome] || ''}`}>
                        {p.funding_outcome}
                      </span>
                    )}
                    <span className="td-muted">{timeAgo(p.updated_at)}</span>
                  </div>
                  <div className="td-work-title">{p.title}</div>
                  {p.nofo_number && <span className="td-work-nofo">{p.nofo_number}</span>}
                  <span className="td-work-stage">{STAGE_LABELS[p.current_stage] || p.current_stage}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Active paper drafts */}
        <div className="td-section">
          <h3><FiEdit /> Paper Drafts</h3>
          {drafts.length === 0 ? (
            <p className="td-empty">No paper drafts yet</p>
          ) : (
            <div className="td-card-list">
              {drafts.map(d => (
                <div key={d.id} className="td-work-card">
                  <div className="td-work-top">
                    <span className="td-work-owner">{d.owner_username}</span>
                    <span className="td-muted">{timeAgo(d.updated_at)}</span>
                  </div>
                  <div className="td-work-title">{d.title}</div>
                  <span className="td-work-stage">{STAGE_LABELS[d.current_stage] || d.current_stage}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Outstanding / overdue items */}
        <div className="td-section">
          <h3><FiClock /> Outstanding Tasks</h3>
          {outstanding_items.length === 0 ? (
            <p className="td-empty">No outstanding tasks</p>
          ) : (
            <ul className="td-task-list">
              {outstanding_items.map(item => (
                <li key={item.id} className={`td-task ${item.is_overdue ? 'td-task--overdue' : ''}`}>
                  <span className={`td-priority td-priority--${item.priority}`} />
                  <div className="td-task-body">
                    <span className="td-task-title">{item.title}</span>
                    <div className="td-task-meta">
                      {item.assignee_username && <span>→ {item.assignee_username}</span>}
                      {item.due_date && (
                        <span className={item.is_overdue ? 'td-overdue' : 'td-muted'}>
                          {item.is_overdue ? 'Overdue' : `Due ${new Date(item.due_date).toLocaleDateString()}`}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Monitor alerts */}
        {monitor_alerts.length > 0 && (
          <div className="td-section">
            <h3><FiBell /> Monitor Alerts</h3>
            <ul className="td-alert-list">
              {monitor_alerts.map((a, i) => (
                <li key={i} className="td-alert">
                  <strong>{a.monitor_name}</strong>
                  <span className="td-muted">by {a.owner_username}</span>
                  <span className="td-alert-count">{a.new_papers_count} new papers</span>
                </li>
              ))}
            </ul>
          </div>
        )}

      </div>

      {/* Member Portfolios */}
      {portfolios && portfolios.length > 0 && (
        <div className="td-section" style={{ gridColumn: '1 / -1' }}>
          <h3><FiUsers /> Member Portfolios</h3>
          <div className="td-portfolio-grid">
            {portfolios.map(m => (
              <div
                key={m.user_id}
                className={`td-portfolio-card ${selectedMember === m.user_id ? 'td-portfolio-card--selected' : ''}`}
                onClick={() => setSelectedMember(selectedMember === m.user_id ? null : m.user_id)}
              >
                <div className="td-portfolio-header">
                  <strong>{m.display_name}</strong>
                  {m.career_stage && (
                    <span className={`td-count-badge ${m.career_stage === 'esi' ? '' : 'td-count-badge--done'}`}>
                      {m.career_stage === 'esi' ? 'ESI' : 'Established'}
                    </span>
                  )}
                </div>
                {m.institution && <span className="td-muted">{m.institution}</span>}
                {m.expertise && <p className="td-portfolio-expertise">{m.expertise.slice(0, 120)}{m.expertise.length > 120 ? '...' : ''}</p>}
                <div className="td-portfolio-stats">
                  <span>{m.collections.length} collections</span>
                  <span>{m.proposals.length} proposals</span>
                  <span>{m.drafts.length} papers</span>
                </div>

                {selectedMember === m.user_id && (
                  <div className="td-portfolio-detail">
                    {m.proposals.length > 0 && (
                      <div className="td-portfolio-list">
                        <strong>Proposals</strong>
                        {m.proposals.map(p => (
                          <div key={p.id} className="td-portfolio-item">
                            <span>{p.title}</span>
                            <span className="td-count-badge">{p.current_stage}</span>
                            {p.funding_outcome && (
                              <span className={`td-count-badge ${p.funding_outcome === 'funded' ? 'td-count-badge--funded' : ''}`}>
                                {p.funding_outcome}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {m.drafts.length > 0 && (
                      <div className="td-portfolio-list">
                        <strong>Papers</strong>
                        {m.drafts.map(d => (
                          <div key={d.id} className="td-portfolio-item">
                            <span>{d.title}</span>
                            {d.target_venue && <span className="td-muted">{d.target_venue}</span>}
                            <span className="td-count-badge">{d.current_stage}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDashboard;
